import { useState, useEffect, useRef, memo } from "react";
import "../../../../css/questionModal.css";
import ModalWrapper from "js/Component/ModalWrapper";
import toast from "react-hot-toast";

/**
 * AddEditFinishingPageModal component for adding/editing finishing page content for form field options
 */
const AddEditFinishingPageModal = (props) => {
    const { 
        isVisible, 
        onClose, 
        selectedOption,
        onSave
    } = props;

    const [finishingDesc, setFinishingDesc] = useState("");
    const [finishingImage, setFinishingImage] = useState(null);
    const filePickerRef = useRef(null);

    // Load existing finishing page data
    useEffect(() => {
        if (isVisible && selectedOption) {
            setFinishingDesc(selectedOption.finishingDesc || "");
            setFinishingImage(selectedOption.finishingImage || null);
        } else {
            // Reset form when modal is closed or no option selected
            setFinishingDesc("");
            setFinishingImage(null);
        }
    }, [isVisible, selectedOption]);

    const isFormValid = () => {
        return finishingDesc && finishingDesc.trim().length > 0;
    };

    const handleImageChangeHandler = (e) => {
        if (e?.target?.files?.length > 0) {
            const file = e.target.files[0];
            const allowedTypes = ["image/jpeg", "image/png"];
            const maxSizeInBytes = 1 * 1024 * 1024; // 1 MB

            if (!allowedTypes.includes(file.type)) {
                toast.error("Only PNG and JPG files are allowed.");
                return;
            }

            if (file.size > maxSizeInBytes) {
                toast.error("Image size must be less than 1 MB.");
                return;
            }
            setFinishingImage(file);
        }
    };

    const handleImagePick = () => {
        if (filePickerRef.current) {
            filePickerRef.current.value = null;
            filePickerRef.current.click();
        }
    };

    const handleRemoveImage = () => {
        setFinishingImage("remove");
    };

    const getImagePreview = () => {
        if (finishingImage?.name) {
            return URL.createObjectURL(finishingImage);
        } else if (finishingImage) {
            return finishingImage;
        }
        return null;
    };

    const onSaveHandler = () => {
        if (!isFormValid() || !selectedOption) return;

        // Pass the finishing page data back to parent
        if (onSave) {
            onSave(selectedOption.id, {
                finishingDesc: finishingDesc.trim(),
                finishingImage: finishingImage
            });
        }
        
        onCloseHandler();
    };

    const onCloseHandler = () => {
        onClose();
        resetForm();
    };

    const resetForm = () => {
        setFinishingDesc("");
        setFinishingImage(null);
    };

    const onCancelHandler = () => {
        onCloseHandler();
    };

    if (!isVisible) return null;

    const saveButtonEnabled = isFormValid();
    const imagePreview = getImagePreview();

    return (
        <ModalWrapper>
            <div className="modal-header">
                <h1 className="modal-title">
                    {selectedOption?.finishingDesc ? "Edit finishing page" : "Add finishing page"}
                </h1>
                <button 
                    className="close-button" 
                    onClick={onCloseHandler}
                >
                    &times;
                </button>
            </div>
            <div className="modal-body" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px'
            }}>
                <label className="input-label">Finishing Page Description *</label>
                <textarea
                    className="question-title-input"
                    value={finishingDesc}
                    onChange={(e) => {
                        setFinishingDesc(e.target.value);
                    }}
                    placeholder="Enter finishing page description..."
                    rows={4}
                    style={{
                        resize: 'vertical',
                        minHeight: '80px'
                    }}
                />

                <div style={{ marginTop: "20px" }}>
                    <label className="input-label">Finishing Page Image (Optional)</label>
                    
                    <div style={{ marginTop: "10px" }}>
                        {imagePreview ? (
                            <div style={{ marginBottom: "10px" }}>
                                <img
                                    src={imagePreview}
                                    alt="Finishing page preview"
                                    style={{
                                        maxWidth: "200px",
                                        maxHeight: "150px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        border: "1px solid #ddd"
                                    }}
                                />
                                <div style={{ marginTop: "8px" }}>
                                    <button 
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary me-2"
                                        onClick={handleImagePick}
                                        style={{marginRight:"20px"}}
                                    >
                                        Change Image
                                    </button>
                                    <button 
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={handleRemoveImage}
                                    >
                                        Remove Image
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button 
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={handleImagePick}
                            >
                                <i className="fa fa-image me-2"></i>
                                Upload Image
                            </button>
                        )}
                    </div>
                </div>

                <input
                    ref={filePickerRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChangeHandler}
                />

                <div className="modal-actions" style={{ marginTop: "30px" }}>
                    <button 
                        className="cancel-btn" 
                        onClick={onCancelHandler}
                    >
                        Cancel
                    </button>
                    <button 
                        className="save-btn" 
                        onClick={onSaveHandler} 
                        disabled={!saveButtonEnabled} 
                        style={{ 
                            cursor: saveButtonEnabled ? "pointer" : "not-allowed", 
                            opacity: saveButtonEnabled ? 1 : 0.6 
                        }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};

export default memo(AddEditFinishingPageModal);